import React, { useState, useEffect } from 'react';
import { useGameStore } from '../store/gameStore';
import { CheckIcon, XIcon } from './Icons';

export const CountCheckModal: React.FC = () => {
  const { countCheck, submitCountCheck } = useGameStore();
  const [inputValue, setInputValue] = useState('');

  // Reset input when modal opens
  useEffect(() => {
    if (countCheck.isActive) {
      setInputValue('');
    }
  }, [countCheck.isActive]);

  if (!countCheck.isActive) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const answer = inputValue.trim() === '' ? null : parseInt(inputValue, 10);
    if (inputValue.trim() !== '' && isNaN(answer as number)) return;
    submitCountCheck(answer);
  };

  const handleSkip = () => {
    submitCountCheck(null);
  };

  // Quick answer buttons for common counts
  const quickAnswers = [-5, -4, -3, -2, -1, 0, 1, 2, 3, 4, 5];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4" style={{ paddingBottom: 'max(1rem, calc(env(safe-area-inset-bottom) + 200px))' }}>
      <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-6 sm:p-8 max-w-md w-full shadow-2xl border-2 border-yellow-500 max-h-[80vh] overflow-y-auto">
        {countCheck.result === null ? (
          // Question state
          <>
            <div className="text-center mb-6">
              <div className="text-4xl mb-3">🧠</div>
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                Count Check!
              </h2>
              <p className="text-gray-400">
                What's the current running count?
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Quick answer buttons */}
              <div className="flex flex-wrap justify-center gap-2 mb-4">
                {quickAnswers.map((num) => (
                  <button
                    key={num}
                    type="button"
                    onClick={() => {
                      setInputValue(num.toString());
                      submitCountCheck(num);
                    }}
                    className="w-10 h-10 rounded-lg bg-gray-700 hover:bg-blue-600 text-white font-bold transition-colors"
                  >
                    {num > 0 ? `+${num}` : num}
                  </button>
                ))}
              </div>

              {/* Manual input */}
              <div className="flex gap-2">
                <input
                  type="number"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="Enter count..."
                  className="flex-1 px-4 py-3 bg-gray-700 text-white rounded-lg text-xl font-bold text-center focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  autoFocus
                />
                <button
                  type="submit"
                  className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-bold transition-colors"
                >
                  Submit
                </button>
              </div>

              {/* Skip button */}
              <button
                type="button"
                onClick={handleSkip}
                className="w-full px-4 py-2 bg-gray-600 hover:bg-gray-500 text-gray-300 rounded-lg font-semibold transition-colors"
              >
                I don't know
              </button>
            </form>
          </>
        ) : (
          // Result state
          <div className="text-center">
            <div className="mb-4 flex justify-center items-center gap-2">
              {countCheck.result === 'correct' ? (
                <CheckIcon size={32} className="text-green-400 drop-shadow" />
              ) : countCheck.result === 'incorrect' ? (
                <XIcon size={32} className="text-red-400 drop-shadow" />
              ) : (
                <div className="text-3xl text-yellow-300">?</div>
              )}
              <h2 className={`text-2xl sm:text-3xl font-bold ${
                countCheck.result === 'correct' ? 'text-green-400' :
                countCheck.result === 'incorrect' ? 'text-red-400' : 'text-yellow-400'
              }`}>
                {countCheck.result === 'correct' ? 'Correct!' :
                 countCheck.result === 'incorrect' ? 'Incorrect' : 'Skipped'}
              </h2>
            </div>
            <p className="text-gray-300 text-lg">
              The count was: <span className="font-bold text-white text-2xl">
                {countCheck.correctAnswer > 0 ? '+' : ''}{countCheck.correctAnswer}
              </span>
            </p>
            {countCheck.result === 'incorrect' && countCheck.userAnswer !== null && (
              <p className="text-gray-400 mt-2">
                You answered: {countCheck.userAnswer > 0 ? '+' : ''}{countCheck.userAnswer}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

